import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import './AdminErrorLogs.css';

function AdminErrorLogs() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotification();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState({
    level: '',
    resolved: '',
    search: '',
  });

  const { data: logsData, isLoading } = useQuery({
    queryKey: ['admin-error-logs', filters, page, limit],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filters.level) params.append('level', filters.level);
        if (filters.resolved) params.append('resolved', filters.resolved);
        if (filters.search) params.append('search', filters.search);
        params.append('page', page.toString());
        params.append('limit', limit === 'all' ? '999999' : limit.toString());
        const res = await apiClient.get(`/admin/error-logs?${params.toString()}`);
        return res.data;
      } catch (error) {
        if (error.response?.status === 500) {
          return { logs: [], pagination: { page: 1, pages: 1, total: 0 } };
        }
        throw error;
      }
    },
    retry: false, // Nekartoti jei klaida
  });

  const logs = logsData?.logs || [];
  
  // Client-side filtering for search (if backend doesn't fully support it)
  const filteredLogs = filters.search 
    ? logs.filter(log => 
        log.message?.toLowerCase().includes(filters.search.toLowerCase()) ||
        log.url?.toLowerCase().includes(filters.search.toLowerCase()) ||
        log.user_id?.toString().includes(filters.search)
      )
    : logs;

  const resolveMutation = useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.put(`/admin/error-logs/${id}/resolve`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-error-logs']);
      success('Klaida pažymėta kaip išspręsta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Nepavyko pažymėti klaidos');
    },
  });

  const getLevelBadge = (level) => {
    const classes = {
      error: 'level-error',
      warning: 'level-warning',
      info: 'level-info',
    };
    return <span className={`level-badge ${classes[level] || ''}`}>{level}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('lt-LT');
  };

  if (isLoading) return <div className="admin-error-logs-page container">Kraunama...</div>;

  return (
    <div className="admin-error-logs-page container">
      <div className="error-logs-header">
        <h1>Klaidos žurnalai</h1>
      </div>

      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Lygis</label>
            <select
              value={filters.level}
              onChange={(e) => setFilters({ ...filters, level: e.target.value })}
            >
              <option value="">Visi</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Statusas</label>
            <select
              value={filters.resolved}
              onChange={(e) => setFilters({ ...filters, resolved: e.target.value })}
            >
              <option value="">Visi</option>
              <option value="false">Neišspręsti</option>
              <option value="true">Išspręsti</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Paieška</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Ieškoti pagal žinutę, URL, vartotoją..."
            />
          </div>
        </div>
      </div>

      <div className="error-logs-list">
        {isLoading ? (
          <div className="loading">Kraunama...</div>
        ) : filteredLogs && filteredLogs.length > 0 ? (
          filteredLogs.map((log) => (
            <div key={log.id} className={`error-log-card ${log.resolved ? 'resolved' : ''}`}>
              <div className="error-log-header">
                <div className="error-log-meta">
                  {getLevelBadge(log.level)}
                  {log.resolved && <span className="resolved-badge">Išspręsta</span>}
                  <span className="error-log-date">{formatDate(log.created_at)}</span>
                </div>
                {!log.resolved && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => resolveMutation.mutate(log.id)}
                  >
                    Pažymėti kaip išspręstą
                  </button>
                )}
              </div>
              <div className="error-log-content">
                <p className="error-message">{log.message}</p>
                {log.url && (
                  <p className="error-detail">
                    <strong>URL:</strong> {log.method} {log.url}
                  </p>
                )}
                {log.status_code && (
                  <p className="error-detail">
                    <strong>Status Code:</strong> {log.status_code}
                  </p>
                )}
                {log.user_id && (
                  <p className="error-detail">
                    <strong>User ID:</strong> {log.user_id}
                  </p>
                )}
                {log.stack && (
                  <details className="error-stack">
                    <summary>Rodyti stack trace</summary>
                    <pre>{log.stack}</pre>
                  </details>
                )}
                {log.context && (
                  <details className="error-context">
                    <summary>Rodyti kontekstą</summary>
                    <pre>{JSON.stringify(log.context, null, 2)}</pre>
                  </details>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>Klaidos žurnaluose nėra.</p>
          </div>
        )}
      </div>

      {/* Puslapiavimas */}
      {logsData?.pagination && (
        <Pagination
          page={logsData.pagination.page}
          totalPages={logsData.pagination.pages}
          totalItems={logsData.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}
    </div>
  );
}

export default AdminErrorLogs;
